import { useMemo } from 'react';

interface DrawdownTableProps {
  returns: number[];
  startDate: string;
}

interface DrawdownPeriod {
  startIdx: number;
  troughIdx: number;
  recoveryIdx: number | null;
  depth: number;
}

const indexToDate = (startDate: string, idx: number): string => {
  const d = new Date(startDate);
  d.setDate(d.getDate() + idx);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: '2-digit' });
};

export default function DrawdownTable({ returns, startDate }: DrawdownTableProps) {
  const periods = useMemo(() => {
    if (returns.length < 2) return [];

    // Build cumulative equity
    const equity = new Array(returns.length + 1);
    equity[0] = 1;
    for (let i = 0; i < returns.length; i++) {
      equity[i + 1] = equity[i] * (1 + returns[i]);
    }

    // Track running peak and detect drawdown periods
    const drawdowns: DrawdownPeriod[] = [];
    let peak = equity[0];
    let peakIdx = 0;
    let inDrawdown = false;
    let currentStart = 0;
    let currentTrough = 0;
    let currentTroughValue = Infinity;

    for (let i = 1; i < equity.length; i++) {
      if (equity[i] >= peak) {
        if (inDrawdown) {
          // Recovered
          drawdowns.push({
            startIdx: currentStart,
            troughIdx: currentTrough,
            recoveryIdx: i,
            depth: (currentTroughValue - equity[peakIdx]) / equity[peakIdx]
          });
          inDrawdown = false;
        }
        peak = equity[i];
        peakIdx = i;
        currentTroughValue = Infinity;
      } else {
        if (!inDrawdown) {
          inDrawdown = true;
          currentStart = peakIdx;
          currentTrough = i;
          currentTroughValue = equity[i];
        }
        if (equity[i] < currentTroughValue) {
          currentTroughValue = equity[i];
          currentTrough = i;
        }
      }
    }

    // Ongoing drawdown
    if (inDrawdown) {
      drawdowns.push({
        startIdx: currentStart,
        troughIdx: currentTrough,
        recoveryIdx: null,
        depth: (currentTroughValue - equity[peakIdx]) / equity[peakIdx]
      });
    }

    // Sort by depth (most negative first), take top 5
    return drawdowns.sort((a, b) => a.depth - b.depth).slice(0, 5);
  }, [returns]);

  if (periods.length === 0) {
    return (
      <div className="bg-gray-900 border border-gray-800 rounded-md p-6">
        <h3 className="text-sm font-medium text-gray-400 mb-4">Top Drawdowns</h3>
        <p className="text-gray-500 text-sm">No drawdown periods detected.</p>
      </div>
    );
  }

  const depthColor = (depth: number): string => {
    const abs = Math.abs(depth);
    if (abs > 0.10) return 'text-orange-400 font-semibold';
    if (abs > 0.05) return 'text-orange-300';
    return 'text-gray-400';
  };

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-md p-4">
      <h3 className="text-sm font-medium text-gray-400 mb-3">Top Drawdowns</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-500 border-b border-gray-800">
              <th className="py-2 px-2 text-left font-medium">#</th>
              <th className="py-2 px-2 text-left font-medium">Start</th>
              <th className="py-2 px-2 text-left font-medium">Trough</th>
              <th className="py-2 px-2 text-left font-medium">Recovery</th>
              <th className="py-2 px-2 text-right font-medium">Depth</th>
              <th className="py-2 px-2 text-right font-medium">Duration</th>
              <th className="py-2 px-2 text-right font-medium">Recovery</th>
            </tr>
          </thead>
          <tbody>
            {periods.map((p, i) => {
              const duration = (p.recoveryIdx ?? returns.length) - p.startIdx;
              const recoveryDays = p.recoveryIdx !== null
                ? p.recoveryIdx - p.troughIdx
                : null;

              return (
                <tr key={i} className="border-b border-gray-800/50">
                  <td className="py-2 px-2 text-gray-500">{i + 1}</td>
                  <td className="py-2 px-2 text-gray-300 tabular-nums">{indexToDate(startDate, p.startIdx)}</td>
                  <td className="py-2 px-2 text-gray-300 tabular-nums">{indexToDate(startDate, p.troughIdx)}</td>
                  <td className="py-2 px-2 tabular-nums">
                    {p.recoveryIdx !== null
                      ? <span className="text-gray-300">{indexToDate(startDate, p.recoveryIdx)}</span>
                      : <span className="text-amber-400">Ongoing</span>
                    }
                  </td>
                  <td className={`py-2 px-2 text-right tabular-nums ${depthColor(p.depth)}`}>
                    {(p.depth * 100).toFixed(2)}%
                  </td>
                  <td className="py-2 px-2 text-right text-gray-300 tabular-nums">{duration}d</td>
                  <td className="py-2 px-2 text-right tabular-nums">
                    {recoveryDays !== null
                      ? <span className="text-gray-300">{recoveryDays}d</span>
                      : <span className="text-amber-400">-</span>
                    }
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
